import React, { useRef, useState, useEffect, useCallback } from 'react';
import { MicIcon } from '../components/Icons';

const MediaRecorderModal = ({ type, onClose, onMediaCaptured }) => {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const getMedia = useCallback(async () => {
    cleanupStream();
    try {
      const constraints = {
        audio: true,
        video: type === 'video' || type === 'photo',
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing media devices.", err);
      setError("Could not access camera/microphone. Please check permissions and try again.");
    }
  }, [type, cleanupStream]);

  useEffect(() => {
    getMedia();
    return cleanupStream;
  }, [getMedia, cleanupStream]);

  const handleCapture = (blob) => {
    const extension = type === 'photo' ? 'jpg' : (type === 'video' ? 'webm' : 'mp3');
    const fileName = `recording-${Date.now()}.${extension}`;
    const file = new File([blob], fileName, { type: blob.type });
    onMediaCaptured(file);
    cleanupStream();
  };

  const startRecording = () => {
    if (streamRef.current) {
      setIsRecording(true);
      const stream = streamRef.current;

      // Determine supported MIME type
      let mimeType = 'video/webm';
      if (type === 'video') {
        const types = [
          'video/webm;codecs=vp9',
          'video/webm;codecs=vp8',
          'video/webm',
          'video/mp4'
        ];
        for (const t of types) {
          if (MediaRecorder.isTypeSupported(t)) {
            mimeType = t;
            break;
          }
        }
      } else {
        // Audio fallback
        mimeType = 'audio/webm';
      }

      try {
        mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      } catch (e) {
        console.error("MediaRecorder creation failed with mimeType:", mimeType, e);
        // Fallback to default if specific type fails
        mediaRecorderRef.current = new MediaRecorder(stream);
      }

      const chunks = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        handleCapture(blob);
      };
      mediaRecorderRef.current.start();
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        handleCapture(blob);
      }, 'image/jpeg');
    }
  };

  const handleClose = () => {
    cleanupStream();
    onClose();
  };

  const renderContent = () => {
    if (error) return <p className="text-red-400">{error}</p>;
    if (type === 'audio') {
      return <div className="flex flex-col items-center justify-center h-full"><MicIcon className={`w-24 h-24 text-gray-500 ${isRecording ? 'text-red-500 animate-pulse' : ''}`} /> <p className="mt-4">{isRecording ? 'Recording...' : 'Ready to record audio'}</p></div>;
    }
    return <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={handleClose}>
      <div className="bg-[#1E1E1E] border border-[#D4AF37]/50 rounded-xl shadow-2xl w-full max-w-2xl m-4 text-[#EAEAEA] p-8 relative" onClick={e => e.stopPropagation()}>
        <button onClick={handleClose} className="absolute top-4 right-4 text-3xl text-[#A0A0A0] hover:text-[#EAEAEA] z-20">&times;</button>
        <h2 className="text-3xl font-bold text-[#D4AF37] mb-6 text-center capitalize">{type} Recorder</h2>
        <div className="bg-black h-96 flex items-center justify-center text-gray-500 rounded-lg overflow-hidden">
          {renderContent()}
        </div>
        <div className="mt-6 flex justify-center space-x-4">
          {!isRecording && type === 'photo' && <button onClick={takePhoto} className="bg-white text-black font-bold py-3 px-8 rounded-full">Take Photo</button>}
          {!isRecording && (type === 'video' || type === 'audio') && <button onClick={startRecording} className="bg-red-600 text-white font-bold py-3 px-8 rounded-full">Start Recording</button>}
          {isRecording && <button onClick={stopRecording} className="bg-white text-black font-bold py-3 px-8 rounded-full">Stop Recording</button>}
        </div>
      </div>
    </div>
  );
};

export default MediaRecorderModal;
